// Wall panel setup — design the fixed button sheet for THE CURRENT LOCATION.
//
// One location, one grid, buttons in fixed slots. Pick a size (4/6/8/12/16/24),
// tap an empty slot to put a device on it, tap a filled one to change it.
//
// Scoping: the panel belongs to the location currently selected in the header,
// and is stored as "#LOC:<id>:<name>" — see core/wall/wallLocation for why the
// location lives in the name rather than a column.
//
// ⚠️ EDITING IS THE LOCATION OWNER'S (or a Super Admin's). A wall sheet is
// shared hardware on a wall: anyone who can reach the tablet can PRESS the
// buttons, but rearranging them is the owner's call.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, MenuItem, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, CircularProgress, ToggleButton,
  ToggleButtonGroup, IconButton,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../core/state/AppContext';
import {
  GRID_SIZES, columnsFor, rowsFor, normaliseSize, slotToPos, layoutButtons,
  buildLocationPanelName, panelsForLocation,
} from '../../core/wall/wallLocation';
import {
  fetchPanels, createPanel, editPanel, saveButton, removeButton, setButtonText,
} from '../desktop/pages/wall/wallApi';
import { getRooms, getOutputs, getDevicesByLocation } from '../../core/api/api';
import { relabelPanel } from '../../core/wall/wallLabel';
import { devicesIn, COMMON_ROOM_ID } from '../../core/devices/deviceAdmin';
import {
  KIND_TOGGLE, KIND_MOMENTARY, KIND_FAN, KIND_CURTAIN, KIND_LOCK, KIND_LIGHT,
  KIND_METER, isLightKey, lightPresetsFor,
  CURTAIN_ACTIONS, FAN_STEPS, onMessage, defaultButtonLabel, fanStepsFor,
} from '../../core/wall/wallButtons';
import { deviceTypeKey, unlockCommand } from '../../core/devices/deviceMeta';
import { channelsFor, fanChannelsFor } from '../../core/devices/statusFormat';
import { fetchRoomScenes } from '../desktop/pages/scenes/scenesApi';
import { resolveSceneOwner } from '../../core/scenes/sceneOwner';
import { FIT_XS } from './fitDialog';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);

export default function WallPanelSetupScreen() {
  const {
    userId, locationId, selectedLocation, isSuperAdmin, canReorder,
  } = useApp();

  const [panel, setPanel] = useState(null);
  const [size, setSize] = useState(8);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rooms, setRooms] = useState([]);
  const [devices, setDevices] = useState([]);

  const [slot, setSlot] = useState(null);      // index being edited
  const [draft, setDraft] = useState(null);    // { ButtonText, uno, message, ButtonColor }
  const [outputs, setOutputs] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [saving, setSaving] = useState(false);

  // ⚠️ THE WALL-MODE SWITCH MOVED TO THE PANEL. This screen answers one
  // question — which buttons, doing what — and the panel answers the other:
  // whether this screen IS the panel. They were on the same page and read as
  // one setting with two halves.
  const navigate = useNavigate();

  // Only the owner (or a Super Admin) may rearrange a shared wall sheet.
  const mayEdit = isSuperAdmin || canReorder;

  // uno -> { roomName, deviceName } for the whole location, and uno -> outputs.
  // Held in refs rather than state: they are only ever read while composing
  // labels, so re-rendering on them would be churn for nothing.
  const deviceIndexRef = useRef(null);
  const outputsRef = useRef(new Map());

  // ONE call answers room and device name for every device in the location
  // (GetAllDevicesByLocationID), so a sheet of twenty-four tiles costs one
  // request rather than twenty-four. RoomName is null for common devices —
  // a real value meaning "belongs to the location", not a missing one.
  const deviceIndex = useCallback(async () => {
    if (deviceIndexRef.current) return deviceIndexRef.current;
    const rows = asArray(await getDevicesByLocation(locationId).catch(() => null));
    const map = new Map(rows.map((d) => [String(d.UniqueNumber), {
      roomName: String(d.RoomName || '').trim(),
      deviceName: String(d.DeviceName || '').trim(),
    }]));
    deviceIndexRef.current = map;
    return map;
  }, [locationId]);

  const outputsOf = useCallback(async (uno) => {
    const key = String(uno);
    if (outputsRef.current.has(key)) return outputsRef.current.get(key);
    const rows = asArray(await getOutputs(key).catch(() => null));
    outputsRef.current.set(key, rows);
    return rows;
  }, []);

  /**
   * Rebuild the captions for the whole sheet, and write back the ones that
   * changed.
   *
   * This runs on LOAD, not only on save, for two reasons. Whether the room
   * belongs in a caption depends on how many rooms the sheet covers, so adding
   * a button in a second room changes what the OTHER tiles should say — and
   * panels built by the first version of the wizard stored the bare output
   * name ("Light1"), which names nothing on a wall. Opening the setup screen
   * repairs them.
   *
   * Captions the owner typed are recognised and left alone (wallLabel.isAutoLabel).
   */
  const syncLabels = useCallback(async (p) => {
    const buttons = p?.Buttons || [];
    if (!buttons.length) return p;

    const index = await deviceIndex();
    const unoOf = (b) => String(b?.Actions?.[0]?.UniqueNumber || '');
    // Only a toggle names a single output. Fan, curtain, lock and colour-light
    // slots take the whole cell, so their caption is Room · Device and there is
    // no one channel to name.
    const needsOutput = (b) => b.ButtonType === KIND_TOGGLE && unoOf(b);
    await Promise.all(
      [...new Set(buttons.filter(needsOutput).map(unoOf))].map(outputsOf),
    );

    const partsFor = (b) => {
      const uno = unoOf(b);
      const dev = index.get(uno) || {};
      let outputName = '';
      if (needsOutput(b)) {
        // The stored step is the ON message, "<channel>-1" — enough to recover
        // the channel, which is the whole reason a toggle stores it.
        const ch = /^(\d+)-/.exec(String(b.Actions[0].MqttMessage || ''))?.[1];
        const row = (outputsRef.current.get(uno) || [])
          .find((o) => String(o.Output) === String(ch));
        outputName = row?.OutputName || '';
      }
      return { roomName: dev.roomName, deviceName: dev.deviceName, outputName };
    };

    const changes = relabelPanel(buttons, partsFor);
    if (!changes.length) return p;

    // Written only by someone allowed to edit the sheet; everyone else still
    // sees the corrected preview, they just do not persist it.
    if (mayEdit) {
      await Promise.all(changes.map((c) => setButtonText(c.WallButtonId, c.ButtonText)
        .catch(() => {})));
    }
    const byId = new Map(changes.map((c) => [c.WallButtonId, c.ButtonText]));
    return {
      ...p,
      Buttons: buttons.map((b) => (byId.has(b.WallButtonId)
        ? { ...b, ButtonText: byId.get(b.WallButtonId) } : b)),
    };
  }, [deviceIndex, outputsOf, mayEdit]);

  const load = useCallback(async () => {
    if (locationId == null) return;
    setLoading(true);
    setError('');
    try {
      const panels = await fetchPanels(userId);
      const mine = panelsForLocation(panels, locationId);
      const p = mine[0] || null;
      setSize(normaliseSize(p?.ColumnsCount ?? 8));
      setRooms(asArray(await getRooms(userId, locationId).catch(() => null)));
      // Captions are corrected before the grid is shown, so the preview and the
      // wall never disagree.
      setPanel(p ? await syncLabels(p).catch(() => p) : p);
    } catch (e) {
      setError('Could not load this location’s wall panel.');
    } finally {
      setLoading(false);
    }
  }, [userId, locationId, syncLabels]);

  // The index is keyed by location, so switching locations must not carry the
  // previous one's device names into the new sheet's captions.
  useEffect(() => { deviceIndexRef.current = null; outputsRef.current = new Map(); }, [locationId]);

  useEffect(() => { load(); }, [load]);

  const slots = useMemo(() => layoutButtons(panel?.Buttons, size), [panel, size]);

  // The room a draft is pointing at, in words. Common devices have no room, and
  // the label builder drops the blank rather than printing a stray separator.
  const draftRoomName = (rid) => (Number(rid) === COMMON_ROOM_ID
    ? '' : (rooms.find((r) => String(r.RoomID) === String(rid))?.RoomName || ''));

  // The grid size is stored in ColumnsCount — the column already on the table.
  // It holds the BUTTON COUNT, not the column count, because the columns are
  // derived from it (columnsFor) and a count is what the owner chooses.
  const changeSize = async (next) => {
    setSize(next);
    if (panel) await editPanel(panel.WallPanelId, { ColumnsCount: next }).catch(() => {});
  };

  const ensurePanel = async () => {
    if (panel) return panel;
    const name = buildLocationPanelName(locationId, selectedLocation?.LocationName || 'Wall');
    await createPanel(userId, { PanelName: name, ColumnsCount: size, BackColor: '#101418' });
    const panels = await fetchPanels(userId);
    const p = panelsForLocation(panels, locationId)[0] || null;
    setPanel(p);
    return p;
  };

  const openSlot = async (i) => {
    if (!mayEdit) return;
    const existing = slots[i];
    const uno = existing?.Actions?.[0]?.UniqueNumber || '';
    setDraft({
      roomId: '',
      ButtonText: existing?.ButtonText || '',
      uno,
      message: existing?.Actions?.[0]?.MqttMessage || '',
      ButtonColor: existing?.ButtonColor || '#37474F',
      source: 'device',
      kind: existing?.ButtonType || KIND_TOGGLE,
      WallButtonId: existing?.WallButtonId,
    });
    setSlot(i);
    setDevices([]);
    setOutputs(uno ? asArray(await getOutputs(uno).catch(() => null)) : []);
  };

  const pickRoom = async (rid) => {
    setDraft((d) => ({ ...d, roomId: rid, uno: '', message: '' }));
    setOutputs([]);
    // "Common devices" is RoomId 0 and lives in a different table — devicesIn
    // branches on it, so the wizard does not have to.
    setDevices(await devicesIn(userId, locationId, rid).catch(() => []));
    // Scenes live under the LOCATION OWNER, not the signed-in user — see
    // core/scenes/sceneOwner. Reading them as ourselves returns nothing for a
    // location we were invited to.
    if (Number(rid) === COMMON_ROOM_ID) { setScenes([]); return; }
    const ownerId = await resolveSceneOwner(userId, locationId).catch(() => userId);
    setScenes(await fetchRoomScenes(ownerId, rid).catch(() => []));
  };

  const pickDevice = async (uno) => {
    const dev = devices.find((x) => String(x.UniqueNumber) === String(uno));
    // The device's TYPE decides what the button can be: a curtain has no on/off
    // channel to hold, so offering a toggle for it would build a button that
    // can never light up.
    const key = deviceTypeKey(dev?.DeviceID);
    // The device type decides the WHOLE slot. Fan, curtain and lock each take
    // the cell as a unit — a fan ladder, an Open/Stop/Close cluster, a guarded
    // lock — so there is no action to pick and none is offered.
    // Colour lights (24 rgb / 25 wwh / 23 wwrgb) take the slot as their preset
    // set, the same way a fan takes it as its ladder.
    // ⚠️ A METER IS NOT A TOGGLE. Current sensors fell through to KIND_TOGGLE,
    // which drew a tile stuck on "—" and published `1-1` at a device with no
    // relay when pressed. They take the slot as a readout instead.
    const kind = isLightKey(key) ? KIND_LIGHT : ({
      fan: KIND_FAN,
      curtain: KIND_CURTAIN,
      door: KIND_LOCK,
      singlePhase: KIND_METER,
      threePhase: KIND_METER,
    }[key] || KIND_TOGGLE);
    const whole = kind !== KIND_TOGGLE;
    // eslint-disable-next-line no-nested-ternary
    const seed = kind === KIND_FAN ? FAN_STEPS[0].message
      : kind === KIND_CURTAIN ? CURTAIN_ACTIONS[0].message
        : kind === KIND_LOCK ? unlockCommand(dev?.DeviceID)
          // The seed's WIDTH is what records which preset set this is — see
          // wallButtons.lightTarget.
          : kind === KIND_LIGHT ? (lightPresetsFor(key)[0]?.command || '') : '';
    setDraft((d) => ({
      ...d,
      uno,
      kind,
      whole,
      // A whole-slot kind still stores ONE step, only to record which device it
      // drives; the messages themselves come from the kind.
      deviceId: dev?.DeviceID,
      message: seed,
      ButtonText: d.ButtonText || (whole
        ? defaultButtonLabel({ roomName: draftRoomName(d.roomId), deviceName: dev?.DeviceName })
        : ''),
    }));
    setOutputs(uno ? asArray(await getOutputs(uno).catch(() => null)) : []);
  };

  const saveSlot = async () => {
    setSaving(true);
    setError('');
    try {
      const p = await ensurePanel();
      if (!p) throw new Error('no panel');
      const { x, y } = slotToPos(slot, size);
      await saveButton(userId, {
        WallButtonId: draft.WallButtonId,
        WallPanelId: p.WallPanelId,
        ButtonText: draft.ButtonText,
        ButtonColor: draft.ButtonColor,
        ButtonType: draft.kind || KIND_MOMENTARY,
        PosX: x,
        PosY: y,
        SortOrder: slot,
        Actions: draft.source === 'scene'
          // A scene's steps are COPIED, not referenced: the wall button then
          // works even if the scene is later edited or deleted, and the runtime
          // needs no second lookup at press time.
          ? (draft.sceneSteps || []).map((st) => ({
            ActionType: 'mqtt', UniqueNumber: st.uniqueNumber, MqttMessage: st.message,
          }))
          /*
           * ⚠️ A METER HAS NO MESSAGE, AND STILL NEEDS ITS STEP. The condition
           * used to be `uno && message`, so a current sensor — which has nothing
           * to publish — saved with NO actions, and `meterTarget` then had no
           * device to read. The step is what records which device the slot is
           * about; the message is only what pressing it would send.
           *
           * Storing a blank message is safe: `wallApi.runSequence` publishes
           * only when `step.MqttMessage` is truthy, and a meter tile is a
           * readout with nothing to press in the first place.
           */
          : (draft.uno
            ? [{ ActionType: 'mqtt', UniqueNumber: draft.uno, MqttMessage: draft.message || '' }]
            : []),
      });
      setSlot(null);
      await load();
    } catch (e) {
      setError('Could not save that button.');
    } finally {
      setSaving(false);
    }
  };

  const clearSlot = async () => {
    if (!draft?.WallButtonId) { setSlot(null); return; }
    setSaving(true);
    try {
      await removeButton(draft.WallButtonId);
      setSlot(null);
      await load();
    } catch (e) {
      setError('Could not remove that button.');
    } finally {
      setSaving(false);
    }
  };

  if (locationId == null) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Choose a location first.</Alert></Box>;
  }

  return (
    // The sheet must FIT — it is a preview of a wall panel, and a preview you
    // have to scroll is not showing you what the wall will look like. The page
    // is a fixed-height column: header row, then the grid taking whatever is
    // left.
    //
    // ⚠️ 100% AND NOT 100vh. AppLayout is already a 100vh column with the header
    // above this and a flex content area below it, so a 100vh child overflows by
    // exactly the header's height — which is the scrollbar that appeared.
    <Box sx={{
      p: 1.5, height: '100%', display: 'flex', flexDirection: 'column',
      maxWidth: 1200, mx: 'auto', overflow: 'hidden',
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {selectedLocation?.LocationName || 'Wall panel'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small" variant="outlined" startIcon={<PlayArrowIcon />}
          onClick={() => navigate('/wall')} sx={{ textTransform: 'none' }}
        >
          Open panel
        </Button>
        {/* The "use this screen as a panel" switch is NOT here any more — it is
            on the panel itself, which is where you are when you decide it. This
            screen is now only: how many buttons, and what each one does. */}
        <ToggleButtonGroup
          size="small" exclusive value={size}
          onChange={(_, v) => v && changeSize(v)}
          disabled={!mayEdit}
        >
          {GRID_SIZES.map((s) => (
            <ToggleButton key={s} value={s} sx={{ px: 1.25 }}>{s}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {!mayEdit && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
          Only this location’s owner can change the panel.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>{error}</Alert>}

      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 1.25, bgcolor: '#101418',
            flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden',
          }}
        >
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columnsFor(size)}, 1fr)`,
            // Equal rows over the available height rather than an aspect ratio:
            // the tiles resize to the window instead of the window growing to
            // fit the tiles, which is what produced a scrollbar at 24 buttons.
            gridTemplateRows: `repeat(${rowsFor(size)}, 1fr)`,
            gap: 1,
            flex: 1, minHeight: 0,
          }}
          >
            {slots.map((b, i) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                onClick={() => openSlot(i)}
                sx={{
                  borderRadius: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  textAlign: 'center', p: 0.5, minHeight: 0, minWidth: 0,
                  fontSize: 'clamp(10px, 1.6vw, 18px)', overflow: 'hidden',
                  cursor: mayEdit ? 'pointer' : 'default',
                  bgcolor: b ? (b.ButtonColor || '#37474F') : 'transparent',
                  border: b ? 'none' : '1px dashed rgba(255,255,255,0.25)',
                  color: b ? (b.TextColor || '#fff') : 'rgba(255,255,255,0.4)',
                }}
              >
                {b ? (
                  <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                    {b.ButtonText || '—'}
                  </Typography>
                ) : <AddIcon fontSize="small" />}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Dialog open={slot != null} onClose={() => setSlot(null)} {...FIT_XS}>
        <DialogTitle sx={{ pb: 0.5 }}>{`Button ${(slot ?? 0) + 1}`}</DialogTitle>
        <DialogContent>
          {/* Room -> Device -> Button. The hierarchy people actually think in;
              a flat list of every device at a large site is unusable. */}
          <TextField
            select size="small" fullWidth label="Room" value={draft?.roomId ?? ''}
            onChange={(e) => pickRoom(e.target.value)}
            sx={{ mb: 1.5, mt: 0.5 }}
          >
            <MenuItem value={COMMON_ROOM_ID}>Common devices</MenuItem>
            {rooms.map((r) => (
              <MenuItem key={r.RoomID} value={r.RoomID}>{r.RoomName || `Room ${r.RoomID}`}</MenuItem>
            ))}
          </TextField>

          {draft?.source !== 'scene' && (
          <TextField
            select size="small" fullWidth label="Device" value={draft?.uno || ''}
            onChange={(e) => pickDevice(e.target.value)}
            disabled={draft?.roomId === '' || draft?.roomId == null}
            sx={{ mb: 1.5 }}
          >
            {devices.map((d) => (
              <MenuItem key={d.UniqueNumber} value={String(d.UniqueNumber)}>
                {d.DeviceName || `Device ${d.UniqueNumber}`}
              </MenuItem>
            ))}
          </TextField>
          )}

          {/* A slot can drive a DEVICE or run a SCENE. Both end up as the same
              row; a scene simply copies the steps it already has. */}
          <ToggleButtonGroup
            size="small" exclusive value={draft?.source || 'device'}
            onChange={(_, v) => v && setDraft((d) => ({ ...d, source: v, uno: '', message: '', sceneId: '' }))}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="device" sx={{ px: 1.5 }}>Device</ToggleButton>
            <ToggleButton value="scene" sx={{ px: 1.5 }}>Scene</ToggleButton>
          </ToggleButtonGroup>

          {draft?.source === 'scene' ? (
            <TextField
              select size="small" fullWidth label="Scene" value={draft?.sceneId || ''}
              onChange={(e) => {
                const sc = scenes.find((x) => String(x.id) === String(e.target.value));
                setDraft((d) => ({
                  ...d,
                  sceneId: e.target.value,
                  kind: KIND_MOMENTARY,
                  sceneSteps: sc?.steps || [],
                  ButtonText: d.ButtonText || sc?.name || '',
                  ButtonColor: sc?.color || d.ButtonColor,
                }));
              }}
              disabled={draft?.roomId === '' || draft?.roomId == null}
              helperText={scenes.length === 0 && draft?.roomId !== ''
                ? 'This room has no scenes yet.'
                : 'Runs the scene — no on/off state.'}
            >
              {scenes.map((sc) => (
                <MenuItem key={sc.id} value={sc.id}>{sc.name}</MenuItem>
              ))}
            </TextField>
          ) : (
          <>
          {/* What the button DOES depends on the device. A curtain gets three
              momentary buttons; a switch channel gets one that toggles. */}
          {draft?.whole ? (
            <Alert severity="info" sx={{ py: 0 }}>
              {draft.kind === KIND_FAN
                ? 'This slot becomes the full fan ladder — Off, 1, 2, 3, 4, Max.'
                : draft.kind === KIND_CURTAIN
                  ? 'This slot becomes Open · Stop · Close.'
                  : draft.kind === KIND_LIGHT
                    ? 'This slot becomes the light’s colour presets.'
                    : 'This slot asks for your password before it opens the lock.'}
            </Alert>
          ) : draft?.curtain ? (
            <TextField
              select size="small" fullWidth label="Action" value={draft?.message || ''}
              onChange={(e) => setDraft((d) => ({
                ...d,
                message: e.target.value,
                ButtonText: d.ButtonText
                  || CURTAIN_ACTIONS.find((a) => a.message === e.target.value)?.label || '',
              }))}
              helperText="Open, Close and Stop are separate buttons."
            >
              {CURTAIN_ACTIONS.map((a) => (
                <MenuItem key={a.message} value={a.message}>{a.label}</MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              select size="small" fullWidth label="Button" value={draft?.message || ''}
              onChange={(e) => {
                const out = outputs.find((o) => onMessage(o.Output) === e.target.value);
                // A fan-speed choice is "<ch>-10"; a switch is "<ch>-1". The
                // shape of the value is what tells them apart.
                const isFanChoice = /^\d+-(?!0$|1$)\d+$/.test(e.target.value);
                setDraft((d) => ({
                  ...d,
                  kind: isFanChoice ? KIND_FAN : KIND_TOGGLE,
                  message: e.target.value,
                  // "Light1" names nothing on a sheet of twenty — say which
                  // room and which device it is on. Still editable.
                  ButtonText: d.ButtonText || defaultButtonLabel({
                    roomName: draftRoomName(d.roomId),
                    deviceName: devices.find((x) => String(x.UniqueNumber) === String(d.uno))?.DeviceName,
                    outputName: out?.OutputName,
                  }),
                }));
              }}
              disabled={!draft?.uno}
              helperText={draft?.uno ? 'Tapping it turns this on or off.' : 'Pick a device first.'}
            >
              {outputs.map((o) => {
                // On a 7+1 / 7+2 the LAST one or two channels are fan speeds,
                // not switches (statusFormat.fanChannelsFor). Offering those as
                // an on/off toggle would send "8-1" to a fan, which is speed 1
                // — not "on".
                const total = channelsFor(draft?.deviceId) ?? outputs.length;
                const fans = fanChannelsFor(draft?.deviceId);
                const isFanCh = fans > 0 && Number(o.Output) > total - fans;
                const value = isFanCh ? fanStepsFor(Number(o.Output))[0].message
                  : onMessage(o.Output);
                return (
                  <MenuItem key={o.Output} value={value}>
                    {`${o.OutputName || `Button ${o.Output}`}${isFanCh ? ' — fan speeds' : ''}`}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
          </>
          )}

          <TextField
            size="small" fullWidth label="Label" value={draft?.ButtonText || ''}
            onChange={(e) => setDraft((d) => ({ ...d, ButtonText: e.target.value }))}
            sx={{ mt: 1.5 }}
          />

          <TextField
            size="small" type="color" label="Colour" value={draft?.ButtonColor || '#37474F'}
            onChange={(e) => setDraft((d) => ({ ...d, ButtonColor: e.target.value }))}
            sx={{ mt: 1.5, width: 120 }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          {draft?.WallButtonId && (
            <IconButton onClick={clearSlot} disabled={saving} sx={{ mr: 'auto' }} color="error">
              <DeleteOutlineIcon />
            </IconButton>
          )}
          <Button onClick={() => setSlot(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveSlot} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
